import React, { useState, useEffect } from 'react';
import { Clock, Video, Save, Undo, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { analyzeScormModule, editScormTiming, updateSlideName, getScormBackups, restoreScormBackup } from '../services/api';

interface Video {
  fileName: string;
  path: string;
  sizeBytes: number;
  sizeMB: number;
  estimatedDurationSeconds: number;
  estimatedDurationFormatted: string;
}

interface VideoSlide {
  id: string;
  title: string;
  durationInFrames: number;
  durationInSeconds: number;
  durationFormatted: string;
}

interface ScormAnalysis {
  frameRate: number;
  videos: Video[];
  videoSlides: VideoSlide[];
  referencedVideo: string | null;
  globalDuration: {
    frames: number;
    seconds: number;
    formatted: string;
  };
}

interface ScormModule {
  id: number;
  title: string;
  scormVersion: string;
}

interface Backup {
  id: number;
  backup_name: string;
  file_path: string;
  size_bytes: number;
  created_at: string;
  creator: {
    username: string;
    email: string;
  };
}

interface SCORMTimingEditorProps {
  moduleId: number;
  moduleName: string;
  onClose: () => void;
  onSave?: () => void;
}

const SCORMTimingEditor: React.FC<SCORMTimingEditorProps> = ({ 
  moduleId, 
  moduleName, 
  onClose, 
  onSave 
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [module, setModule] = useState<ScormModule | null>(null);
  const [analysis, setAnalysis] = useState<ScormAnalysis | null>(null);
  const [backups, setBackups] = useState<Backup[]>([]);
  
  const [selectedVideo, setSelectedVideo] = useState<string>('');
  const [selectedSlide, setSelectedSlide] = useState<any>(null);
  const [newDuration, setNewDuration] = useState('');
  const [newSlideName, setNewSlideName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Force re-render when selectedSlide changes
  React.useEffect(() => {
    console.log('Selected slide changed to:', selectedSlide);
  }, [selectedSlide]);
  const [customDuration, setCustomDuration] = useState<string>('');
  
  const [showBackups, setShowBackups] = useState(false);
  const [verificationData, setVerificationData] = useState<any>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    loadAnalysis();
    loadBackups();
  }, [moduleId]);

  const loadAnalysis = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await analyzeScormModule(moduleId);
      const { module: moduleData, analysis: analysisData } = response.data;
      
      setModule(moduleData);
      setAnalysis(analysisData);
      
      // Auto-select the referenced video or the middle-duration video
      if (analysisData.referencedVideo) {
        setSelectedVideo(analysisData.referencedVideo);
      } else if (analysisData.videos.length > 0) {
        // Sort by duration and pick middle one
        const sortedVideos = [...analysisData.videos].sort((a, b) => 
          a.estimatedDurationSeconds - b.estimatedDurationSeconds
        );
        const middleIndex = Math.floor(sortedVideos.length / 2);
        setSelectedVideo(sortedVideos[middleIndex].fileName);
      }
      
      // Don't auto-select any slide - let user choose which one to edit
      // This avoids confusion about which slide is selected
      if (analysisData.videoSlides.length > 0) {
        console.log(`Found ${analysisData.videoSlides.length} slides available for editing:`, 
          analysisData.videoSlides.map((s: any) => `${s.title || s.id} (${s.durationFormatted})`));
        setSelectedSlide(null); // No auto-selection
      }
      
    } catch (error: any) {
      console.error('Error loading SCORM analysis:', error);
      setError(error.response?.data?.error || 'Failed to analyze SCORM module');
    } finally {
      setIsLoading(false);
    }
  };

  const loadBackups = async () => {
    try {
      const response = await getScormBackups(moduleId);
      setBackups(response.data);
    } catch (error) {
      console.error('Error loading backups:', error);
    }
  };

  const loadVerification = async () => {
    try {
      setIsVerifying(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4005/api';
      const response = await fetch(`${apiUrl}/scorm-editor/verify/${moduleId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVerificationData(data.verification);
      } else {
        console.error('Failed to load verification data');
      }
    } catch (error) {
      console.error('Error loading verification:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSave = async () => {
    if (!selectedSlide) {
      setError('Please select a slide to update');
      return;
    }

    // Check if this is a name-only update
    const isNameOnlyUpdate = newSlideName && newSlideName.trim() !== '' && (!customDuration || customDuration.trim() === '');
    
    if (isNameOnlyUpdate) {
      // Name-only update
      try {
        setIsSaving(true);
        setError(null);
        setSuccess(null);

        const response = await updateSlideName(moduleId, selectedSlide.id, newSlideName.trim());
        
        const slideNameData = response.data.changes.slideName;
        const filesUpdated = slideNameData.filesUpdated;
        const statusMessage = `Slide found at line ${slideNameData.slideFoundAtLine} of ${slideNameData.totalLines} lines. Files updated: Main file ${filesUpdated?.mainFile ? '✅' : '❌'}, Companion file ${filesUpdated?.companionFile ? '✅' : '❌'}`;
        setSuccess(statusMessage);
        
        // Reload analysis and backups
        await Promise.all([loadAnalysis(), loadBackups()]);
        
        // Call onSave callback if provided
        if (onSave) {
          onSave();
        }
        
      } catch (error: any) {
        console.error('Error updating slide name:', error);
        setError(error.response?.data?.error || 'Failed to update slide name');
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Timing update (with optional name update)
    if (!customDuration) {
      setError('Please enter a duration for the selected slide');
      return;
    }

    // Auto-select video: use referenced video or first available video
    let videoToUse: string | null = selectedVideo;
    if (!videoToUse && analysis) {
      videoToUse = analysis.referencedVideo || (analysis.videos.length > 0 ? analysis.videos[0].fileName : null);
    }

    if (!videoToUse) {
      setError('No video available for this slide');
      return;
    }

    // At this point, videoToUse is guaranteed to be a string
    const selectedVideoFile: string = videoToUse;

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const response = await editScormTiming(moduleId, selectedVideoFile, customDuration, selectedSlide.id, newSlideName);
      
      setSuccess(`Successfully updated timing! Time difference: ${response.data.changes.timeDifference.formatted}`);
      
      // Reload analysis and backups
      await Promise.all([loadAnalysis(), loadBackups()]);
      
      // Call onSave callback if provided
      if (onSave) {
        onSave();
      }
      
    } catch (error: any) {
      console.error('Error saving SCORM timing:', error);
      setError(error.response?.data?.error || 'Failed to update SCORM timing');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = async (backupId: number) => {
    if (!window.confirm('Are you sure you want to restore from this backup? This will overwrite the current module.')) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const response = await restoreScormBackup(moduleId, backupId);
      
      setSuccess(`Successfully restored from backup: ${response.data.restoredFrom}`);
      
      // Reload analysis and backups
      await Promise.all([loadAnalysis(), loadBackups()]);
      
    } catch (error: any) {
      console.error('Error restoring backup:', error);
      setError(error.response?.data?.error || 'Failed to restore from backup');
    } finally {
      setIsSaving(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    } else {
      return `${m}:${s.toString().padStart(2, '0')}`;
    }
  };

  const handleSlideSelect = (slide: any) => {
    setSelectedSlide(slide);
    setCustomDuration(slide.durationFormatted);
    setNewSlideName(slide.title || slide.name || '');
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-white">Analyzing SCORM module...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center">
            <Video className="h-6 w-6 text-blue-400 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-white">SCORM Timing Editor</h2>
              <p className="text-gray-400 text-sm">{moduleName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-100 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        {success && (
          <div className="mx-6 mt-4 p-3 bg-green-500/20 border border-green-500 rounded-lg text-green-100 flex items-center">
            <CheckCircle className="h-5 w-5 mr-2" />
            {success}
          </div>
        )}

        <div className="p-6">
          {/* Debug Info (remove after testing) */}
          <div className="mb-4 p-2 bg-gray-900 rounded text-xs text-gray-400">
                            🔍 Debug: Selected Slide = "{selectedSlide?.id || 'none'}" | Total Slides = {analysis?.videoSlides?.length || 0}
            <br />
            💡 Slides Found: {analysis?.videoSlides?.map(s => s.id).join(', ') || 'None'}
          </div>

          {/* Current Status */}
          {analysis && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white mb-4">Current Status</h3>
              <div className="bg-gray-700 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Currently Referenced Video</p>
                    <p className="text-white font-medium">{analysis.referencedVideo || 'None'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Current Slide Duration</p>
                    <p className="text-white font-medium">
                      {analysis.videoSlides.length > 0 ? analysis.videoSlides[0].durationFormatted : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Slide Selection */}
          {analysis && analysis.videoSlides.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white mb-4">Select Slide to Update</h3>
              <p className="text-gray-400 text-sm mb-4">Choose the slide you want to change the timing for:</p>
              <div className="space-y-3">
                {analysis.videoSlides
                  .sort((a, b) => b.durationInSeconds - a.durationInSeconds) // Longest first
                  .map((slide) => (
                    <label
                      key={slide.id}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-colors block ${
                        selectedSlide?.id === slide.id
                          ? 'border-green-500 bg-green-500/20 ring-2 ring-green-400/50'
                          : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name="slideSelection"
                              value={slide.id}
                              checked={selectedSlide?.id === slide.id}
                              onChange={(e) => {
                                console.log('Radio changed:', slide.id, 'Checked:', e.target.checked);
                                handleSlideSelect(slide);
                              }}
                              className={`mr-3 w-4 h-4 ${
                                selectedSlide === slide.id
                                  ? 'text-green-600 focus:ring-green-500'
                                  : 'text-blue-600 focus:ring-blue-500'
                              }`}
                            />
                            <div>
                              <p className={`font-medium ${
                                selectedSlide?.id === slide.id ? 'text-green-100' : 'text-white'
                              }`}>{slide.title}</p>
                              <p className={`text-sm ${
                                selectedSlide?.id === slide.id ? 'text-green-200' : 'text-gray-400'
                              }`}>
                                Current Duration: {slide.durationFormatted} • ID: {slide.id}
                              </p>
                            </div>
                          </div>
                        </div>
                        {slide.durationInSeconds > 1800 && ( // More than 30 minutes
                          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">
                            Likely Problem Slide
                          </span>
                        )}
                      </div>
                    </label>
                  ))}
              </div>
            </div>
          )}

          {/* New Duration Input - Simple and Clear */}
          {selectedSlide && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white mb-4">Set New Duration</h3>
              <div className="bg-gray-700 p-4 rounded-lg space-y-4">
                {/* Duration Input */}
                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    Enter the correct duration for this slide:
                  </label>
                  <input
                    type="text"
                    value={customDuration}
                    onChange={(e) => setCustomDuration(e.target.value)}
                    placeholder="e.g., 19:32 or 1:23:45"
                    className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    💡 Use MM:SS format (like 19:32) or HH:MM:SS format (like 1:23:45)
                  </p>
                  {analysis && analysis.videoSlides.find(s => s.id === selectedSlide.id) && (
                    <p className="text-xs text-yellow-400 mt-1">
                      Current: {analysis.videoSlides.find(s => s.id === selectedSlide.id)?.durationFormatted}
                    </p>
                  )}
                </div>

                {/* Slide Name Input */}
                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    Update slide name (optional - helps test S3 updates):
                  </label>
                  <input
                    type="text"
                    value={newSlideName}
                    onChange={(e) => setNewSlideName(e.target.value)}
                    placeholder="Enter new slide name"
                    className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    💡 This will help test if S3 updates are working - check if name changes in player
                  </p>
                  <p className="text-xs text-yellow-400 mt-1">
                    Current: {selectedSlide.title}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <div>
              <button
                onClick={() => setShowBackups(!showBackups)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors flex items-center"
              >
                <Undo className="h-4 w-4 mr-2" />
                {showBackups ? 'Hide' : 'Show'} Backups ({backups.length})
              </button>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !selectedSlide || (!customDuration && !newSlideName)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {newSlideName && !customDuration ? 'Update Name' : 'Update Timing'}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Debug Information Section */}
          {analysis && (
            <div className="mt-8 bg-gray-900 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-white">🔧 Debug Information</h3>
                <button
                  onClick={loadVerification}
                  disabled={isVerifying}
                  className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors text-sm disabled:opacity-50"
                >
                  {isVerifying ? 'Verifying...' : 'Verify S3 Files'}
                </button>
              </div>
              
              {/* Verification Results */}
              {verificationData && (
                <div className="mb-6 bg-gray-800 rounded p-4">
                  <h4 className="text-yellow-400 font-medium mb-3">📊 S3 File Verification</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-300">Module ID: {verificationData.moduleId}</p>
                      <p className="text-gray-300">S3 Key: {verificationData.s3Key}</p>
                      <p className="text-gray-300">Timestamp: {new Date(verificationData.timestamp).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-300">Target Slide: Slide51316</p>
                      {verificationData.files.projectTxt && (
                        <p className="text-green-400">
                          project.txt: {verificationData.files.projectTxt.targetSlideSeconds} seconds
                        </p>
                      )}
                      {verificationData.files.projectJs && (
                        <p className="text-blue-400">
                          project.js: {verificationData.files.projectJs.slide51316Duration} frames
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* All Slides Timing */}
                  {verificationData.files.projectTxt?.allSlidesWithTiming && (
                    <div className="mt-3">
                      <p className="text-gray-300 text-sm mb-2">All Slides in S3:</p>
                      <div className="max-h-32 overflow-y-auto bg-gray-700 rounded p-2">
                        {verificationData.files.projectTxt.allSlidesWithTiming
                          .sort((a: any, b: any) => b.seconds - a.seconds)
                          .map((slide: any) => (
                          <div key={slide.id} className={`flex justify-between items-center py-1 px-2 rounded text-xs ${
                            slide.id === 'Slide51316' ? 'bg-green-700' : ''
                          }`}>
                            <span className="text-gray-300 truncate mr-2">{slide.name}</span>
                            <span className="text-gray-400 font-mono">{slide.seconds}s</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-800 p-4 rounded">
                    <h4 className="text-green-400 font-medium mb-2">📄 SCORM Analysis</h4>
                    <p className="text-gray-300">Module ID: {moduleId}</p>
                    <p className="text-gray-300">Module Name: {moduleName}</p>
                    <p className="text-gray-300">Referenced Video: {analysis.referencedVideo || 'None'}</p>
                    <p className="text-gray-300">Frame Rate: {analysis.frameRate} fps</p>
                    <p className="text-gray-300">Total Videos: {analysis.videos?.length || 0}</p>
                    <p className="text-gray-300">Total Slides: {analysis.videoSlides?.length || 0}</p>
                  </div>
                  
                  <div className="bg-gray-800 p-4 rounded">
                    <h4 className="text-blue-400 font-medium mb-2">⏱️ Selected Slide Info</h4>
                    {selectedSlide ? (
                      <>
                        <p className="text-gray-300">Slide ID: {selectedSlide.id}</p>
                        <p className="text-gray-300">
                          Current Duration: {analysis.videoSlides.find(s => s.id === selectedSlide.id)?.durationFormatted || 'Unknown'}
                        </p>
                        <p className="text-gray-300">
                          Frames: {analysis.videoSlides.find(s => s.id === selectedSlide.id)?.durationInFrames || 'Unknown'}
                        </p>
                        <p className="text-gray-300">
                          Name: {selectedSlide.title || selectedSlide.name || 'Unnamed'}
                        </p>
                        {customDuration && (
                          <p className="text-yellow-400">New Duration: {customDuration}</p>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-400">No slide selected</p>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-800 p-4 rounded">
                  <h4 className="text-purple-400 font-medium mb-2">📊 All Slides with Timing</h4>
                  <div className="max-h-40 overflow-y-auto">
                    {analysis.videoSlides
                      .sort((a, b) => b.durationInSeconds - a.durationInSeconds)
                      .map((slide, index) => (
                      <div key={slide.id} className={`flex justify-between items-center py-1 px-2 rounded ${
                        slide.id === selectedSlide?.id ? 'bg-green-700' : ''
                      }`}>
                        <span className="text-gray-300 truncate mr-2 text-xs">{slide.title}</span>
                        <span className="text-gray-400 text-xs font-mono">{slide.durationFormatted}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-gray-800 p-4 rounded">
                  <h4 className="text-orange-400 font-medium mb-2">🎥 Available Videos</h4>
                  <div className="space-y-1">
                    {analysis.videos?.map((video, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-gray-300 text-xs">{video.fileName}</span>
                        <span className="text-gray-400 text-xs">{video.estimatedDurationFormatted}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Backups Section */}
          {showBackups && (
            <div className="mt-6 pt-6 border-t border-gray-700">
              <h3 className="text-lg font-medium text-white mb-4">Available Backups</h3>
              {backups.length === 0 ? (
                <p className="text-gray-400">No backups available</p>
              ) : (
                <div className="space-y-2">
                  {backups.map((backup) => (
                    <div
                      key={backup.id}
                      className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
                    >
                      <div>
                        <p className="text-white font-medium">{backup.backup_name}</p>
                        <p className="text-sm text-gray-400">
                          Created {new Date(backup.created_at).toLocaleString()} by {backup.creator.username}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRestore(backup.id)}
                        disabled={isSaving}
                        className="px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-500 transition-colors text-sm disabled:opacity-50"
                      >
                        Restore
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SCORMTimingEditor; 